import React from 'react';
import { withRouter, Link } from 'react-router-dom';
import { Container, Form } from 'react-bootstrap';
import SignatureCanvas from 'react-signature-canvas';
import moment from 'moment';

// Services & Helpers
import GlobalStateService from 'services/GlobalStateService';
import OnboardingService from 'services/OnboardingService';
import ContractService from 'services/ContractService';
import BootboxHelper from 'helpers/BootboxHelper';
import PageHeader from 'components/common/PageHeader';

// Components
import Loader from 'components/common/Loader';
import ExpandingTextArea from 'react-expanding-textarea';

// Images
import chrisSignatureImg from 'images/chris-signature.jpg';

//-------------------------------------------------------------------------------------------------------------------

class SignContractPage extends React.Component {

    constructor(props) {
        super(props);

        this.pdfURL = `/api/contract/get-pdf?random=${Math.random()}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrollbar=0`;

        this.state = {
            isAgreeTicked: false,
            isLoading: true,
            canvasWidth: 500
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        this.load();
        GlobalStateService.setValues({ area: 'sign-contract' });
    }

    async load() {
        const loginInfo = GlobalStateService.getValue('loginInfo');
        let onboarding = await OnboardingService.get();
        if (!onboarding && loginInfo.instructor) {
            onboarding = {
                instructor: loginInfo.instructor
            };
        };
        const contract = await ContractService.getLatest();
        this.setState({
            isLoading: false,
            onboarding,
            contract
        });
    }

    async submit() {
        const {
            isAgreeTicked,
            isSubmitting,
            onboarding,
            newPassword
        } = this.state;
        if (isSubmitting) {
            return;
        }
        if (!isAgreeTicked) {
            BootboxHelper.alert('Please tick the box to confirm that you agree to the terms.');
            return;
        }
        if (onboarding && !onboarding.isPasswordSet && !newPassword) {
            BootboxHelper.alert('Please provide a password.');
            return;
        }
        if (this.sigCanvasRef.isEmpty()) {
            BootboxHelper.alert('Please provide a signature.');
            return;
        }
        const signature = this.sigCanvasRef.toDataURL('image/png');

        this.sigCanvasRef.off();
        this.setState({ isSubmitting: true });
        try {
            await ContractService.sign(signature, newPassword);
            this.load();

            await BootboxHelper.alert('Thank you for signing the contract. A copy will be sent to you by email for your records.');

            // Redirect them to the main portal
            if (onboarding && onboarding.instructor) {
                window.location.replace(`/instructor/${onboarding.instructor.slug}?first-visit=true`);
            }
            
        } catch (error) {
            BootboxHelper.alert(error);
        } finally {
            this.setState({ isSubmitting: false });
        }
    }

    async confirmClearSignature() {
        const confirm = await BootboxHelper.confirm('Clear signature and start again?');
        if (confirm) {
            this.sigCanvasRef.clear();
        }
    }

    scrollToSignature() {
        const element = document.querySelector('.signatures');
        const rect = element.getBoundingClientRect();
        window.scrollTo({
            top: rect.top - 140,
            behavior: 'smooth'
        });
    }

    //-------------------------------------------------------------------------------------------------------------------
    //  Render
    //-------------------------------------------------------------------------------------------------------------------

    render() {
        const {
            isLoading,
            canvasWidth
        } = this.state;
        
        if (isLoading) {
            return (<Loader />);
        }

        setTimeout(() => {
            const element = document.querySelector('canvas');
            if (element) {
                const rect = element.getBoundingClientRect();
                const width = Math.round(rect.width);
                if (Math.abs(width - canvasWidth) >= 5) {
                    this.setState({ canvasWidth: width });
                }
            }
        });

        return (<>

            <div className="page sign-contract-page">

                <PageHeader>
                    
                    <h1>Authorized Instructor Work Order</h1>
                    
                </PageHeader>

                <main>

                    <Container>

                        {this.renderInner()}

                    </Container>

                </main>

            </div>

        </>);
    }

    renderInner() {
        const {
            contract,
            onboarding,
            isSubmitting,
            newPassword
        } = this.state;
        
        if (!contract) {
            return (
                <section className="contract-section empty-text">
                    {onboarding.instructor.type == 'Candidate' ?
                        <p>We are currently waiting for your company to sign the contract. You will be notified once this has been done.</p> :
                        <p>You do not currently have an contract to sign. If this is in error, please contact support.</p>
                    }
                </section>
            );
        }

        return (<>

            {!onboarding.isPasswordSet &&
                <section className="set-password-section">
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Once you have signed the contract, you will be able to log into the portal.
                            <br />
                            Please provide a password so you can log back in later.<br />
                            Your password must be at least 10 characters long.
                        </Form.Label>
                        <input
                            className="form-control"
                            type="password"
                            value={newPassword || ''}
                            disabled={isSubmitting}
                            onChange={e => this.setState({ newPassword: e.target.value })}
                            placeholder="New password..."
                        />
                    </Form.Group>
                </section>
            }

            <section className="contract-section">

                {!contract.dateSigned &&
                    <Form.Group className="mb-3">
                        <Form.Label>Please enter your {contract.instructor ? 'home' : 'company'} address</Form.Label>
                        <ExpandingTextArea
                            className="form-control"
                            defaultValue={(contract.instructor ? contract.instructor.address : contract.subAgency.address) || ''}
                            onBlur={e => {
                                const newValue = (e.target.value || '');
                                if (
                                    (contract.instructor && newValue != contract.instructor.address) ||
                                    (contract.subAgency && newValue != contract.subAgency.address)
                                ) {
                                    ContractService.updateAddress(newValue);
                                }
                            }}
                            rows={4}
                        />
                    </Form.Group>
                }

                <p className="text-center">
                    This Work Order is effective as of {moment().format('MMMM DD, YYYY')} (“Effective Date”) and is entered into and made a part of the Main Services Contract between go courses and {contract.name}.
                    To apply to go courses entity signing below (for purposes of this Work Order, “go courses”) and {contract.name} (“Contractor”). 
                    <br /><br />
                    {!contract.dateSigned && <>
                        <button type="button" className="btn btn-primary" onClick={() => this.scrollToSignature()}>
                            <span className="fa fa-arrow-down" />{' '}
                            Sign now
                        </button>
                        <br /><br />
                    </>}
                </p>
                
                <table className="table table-bordered contract-terms-table">
                    <thead>
                        <tr>
                            <th>AWS Training Class Name</th>
                            <th>Payment Terms</th>
                            <th>Duration of Class</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>AWS trainings</td>
                            <td>Net {contract.paymentTerms} days</td>
                            <td>1, 2, 3, 4 or 5 days</td>
                        </tr>
                    </tbody>
                </table>

                <table className="table table-bordered contract-instructors-table">
                    <thead>
                        <tr>
                            <th>Instructor</th>
                            <th>Fees</th>
                            <th>Geo(s) permitted</th>
                        </tr>
                    </thead>
                    <tbody>
                        {contract.rateInfos.map(rateInfo =>
                            <tr key={rateInfo.name}>
                                <th>{rateInfo.name}</th>
                                <td>
                                    {rateInfo.rateDescriptions.map((rd, index) =>
                                        <p key={index}>{rd}</p>
                                    )}
                                </td>
                                <td>{rateInfo.geoNames.join(', ')}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                
                <p className="text-center space-around">
                    IN WITNESS WHEREOF, the parties have executed this Work Order as of the Effective Date.
                </p>

                <div className="row signatures">

                    <div className="col-lg-6">

                        <div className="signature-block">

                            <h2>Go courses</h2>

                            <div className="signature-block-inner">

                                <table className="signature-table">
                                    <tbody>
                                        <tr>
                                            <th>Signed:</th>
                                            <td>
                                                <img src={chrisSignatureImg} />
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Printed:</th>
                                            <td>Chris Onslow</td>
                                        </tr>
                                        <tr>
                                            <th>Date signed:</th>
                                            <td>{moment(contract.dateSigned || new Date()).format('DD/MM/YYYY')}</td>
                                        </tr>
                                    </tbody>
                                </table>
                                
                            </div>

                        </div>
                        
                    </div>

                    <div className="col-lg-6">

                        <div className="signature-block">
                            
                            <h2>Contractor</h2>

                            <div className="signature-block-inner">

                                {contract.dateSigned ?
                                    this.renderSigned() :
                                    this.renderNotSigned()
                                }

                            </div>

                        </div>

                    </div>

                </div>

                <p className="text-center">
                    To view the full services contract, <a href={this.pdfURL} target="_blank">click here</a>.
                </p>

            </section>

        </>);
    }

    renderSigned() {
        const { contract } = this.state;
        
        return (<>

            <table className="signature-table">
                <tbody>
                    <tr>
                        <th>Signed:</th>
                        <td>
                            <img src={contract.signature} />
                        </td>
                    </tr>
                    <tr>
                        <th>Printed:</th>
                        <td>
                            {contract.signedByUser && <>
                                {contract.signedByUser.firstName} {contract.signedByUser.lastName}
                            </>}
                        </td>
                    </tr>
                    <tr>
                        <th>Date signed:</th>
                        <td>{moment(contract.dateSigned).format('DD/MM/YYYY')}</td>
                    </tr>
                </tbody>
            </table>
            
        </>);
    }

    renderNotSigned() {
        const { isAgreeTicked, isSubmitting, canvasWidth } = this.state;

        return (<>

            <Form.Check
                id="i-agree"
                type="checkbox"
                className="agree-checkbox"
                checked={isAgreeTicked}
                onChange={e => this.setState({ isAgreeTicked: e.target.checked })}
                disabled={isSubmitting}
                label="I have read the contract and agree to be bound by its terms"
            />

            <div className="form-group">
                <SignatureCanvas
                    ref={(ref) => { this.sigCanvasRef = ref }}
                    penColor="blue"
                    canvasProps={{
                        width: canvasWidth,
                        height: canvasWidth * 2 / 5, // 500 -> 200
                        className: 'signature-canvas'
                    }}
                />

                <p className="explanation">
                    Please use your mouse (or finger/stylus if using a mobile device) to write a signature in the box above.
                </p>

                <div className="controls">

                    <button className="btn btn-secondary me-auto" onClick={() => this.confirmClearSignature()} disabled={isSubmitting}>
                        <span className="fa fa-times" />{' '}
                        Clear signature
                    </button>

                    <button className="btn btn-primary ms-auto" onClick={() => this.submit()} disabled={isSubmitting}>
                        {isSubmitting ? <>
                            <Loader isInline />{' '}
                            Please wait...
                        </> : <>
                            <span className="fa fa-check" />{' '}
                            Accept contract
                        </>}
                    </button>

                </div>

            </div>

        </>);
    }
}

export default withRouter(SignContractPage);